import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdInbox, MdMailOutline, MdMarkEmailRead, MdMenu } from "react-icons/md";
import Layout from "../Layout/Layout";
import { isAuthenticated } from "../auth";
import { getEmailInboxPageAPI, getEmailInboxEmailAPI, markAllEmailInboxReadAPI } from "../auth/apiEmailInbox";
import EmailInboxSidebar from "../pageInside/EmailMarketing/EmailInboxSidebar";
import EmailReceivedView from "../pageInside/EmailMarketing/EmailReceivedView";
import { elakayAutoText } from "../pageInside/dataElakay";
import "../pageInside/EmailMarketing/emailMarketing.css";
import "./emailInboxPage.css";

const PAGE_SIZE = 25;

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
};

const senderDisplayName = (senderId) =>
  senderId?.firstname
    ? `${senderId.firstname} ${senderId.lastname || ""}`.trim()
    : elakayAutoText("h_emk_inbox_elakay_marketing", "Elakay Marketing");

// Gmail/Outlook-inspired, Elakay-styled (spec §18): a real sidebar + main
// pane app shell, not a small centered card -- see emailInboxPage.css's own
// header comment for the full layout rationale (full-height shell, reused
// --emk-* design tokens, no invented dark-mode system).
//
// Routing (spec §17): /email-inbox stays the list; /email-inbox/:emailId
// is new and additive (clean navigation, direct refresh, browser back/
// forward, a shareable-within-the-account URL) -- both render this exact
// same component, which reads `emailId` from the URL instead of local
// state to decide what's open.
//
// Ownership (spec §17/26): every request here is scoped server-side to
// `authenticatedUser._id` (see emailInboxController.js) -- this page never
// sends/needs a userId of its own.
const EmailInboxPage = ({ infoSource }) => {
  const { token } = isAuthenticated() || {};
  const { emailId } = useParams();
  const navigate = useNavigate();

  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [result, setResult] = useState({ items: [], total: 0, totalPages: 1, unreadCount: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openEmail, setOpenEmail] = useState(null);
  const [openError, setOpenError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const load = useCallback(async (pageToLoad, activeFilter) => {
    setLoading(true); setError("");
    try {
      const data = await getEmailInboxPageAPI({ page: pageToLoad, limit: PAGE_SIZE, filter: activeFilter === "all" ? undefined : activeFilter }, token);
      setResult(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(page, filter); }, [load, page, filter]);

  // Opening an email is driven by the URL (spec §17) -- refresh, a direct
  // link, or browser back/forward all resolve to the exact same fetch.
  useEffect(() => {
    if (!emailId) { setOpenEmail(null); setOpenError(""); return; }
    let cancelled = false;
    setOpenEmail(null); setOpenError("");
    getEmailInboxEmailAPI(emailId, token)
      .then((data) => {
        if (cancelled) return;
        setOpenEmail(data.email);
        // Optimistic local update (spec §13/24: no full Inbox refetch just
        // to reflect a read email) -- the server already marked it read
        // via this same GET call; unread badges everywhere else pick this
        // up via the same custom event trashCount/other counters already use.
        setResult((prev) => {
          const wasUnread = prev.items.find((item) => item._id === emailId)?.isRead === false;
          if (!wasUnread) return prev;
          return {
            ...prev,
            items: prev.items.map((item) => (item._id === emailId ? { ...item, isRead: true } : item)),
            unreadCount: Math.max(0, prev.unreadCount - 1),
          };
        });
        window.dispatchEvent(new CustomEvent("elakay:email-inbox-changed"));
      })
      .catch((e) => { if (!cancelled) setOpenError(e.message); });
    return () => { cancelled = true; };
  }, [emailId, token]);

  const markAllRead = async () => {
    try {
      await markAllEmailInboxReadAPI(token);
      setResult((prev) => ({ ...prev, items: prev.items.map((item) => ({ ...item, isRead: true })), unreadCount: 0 }));
      window.dispatchEvent(new CustomEvent("elakay:email-inbox-changed"));
    } catch (e) { setError(e.message); }
  };

  const selectFilter = (nextFilter) => {
    setFilter(nextFilter);
    setPage(1);
    setSidebarOpen(false);
    if (emailId) navigate("/email-inbox");
  };

  const openMail = (id) => navigate(`/email-inbox/${id}`);
  const backToList = () => navigate("/email-inbox");

  const readCount = Math.max(0, result.total - result.unreadCount);
  const isViewingEmail = Boolean(emailId);

  return (
    <Layout
      infoSource={infoSource}
      title={elakayAutoText("h_emk_inbox_page_title", "Email Inbox")}
      loading={false}
      SliderPunShow={false}
      outOffPage={false}
      showFilterMenu={false}
      enableBodyParticles={false}
      enableFloatingSquares={false}
    >
      <div className="emk-scope email-inbox-scope" style={infoSource?.styleNav}>
        <div className="email-inbox-app">
          <EmailInboxSidebar
            activeFilter={filter}
            onSelectFilter={selectFilter}
            unreadCount={result.unreadCount}
            readCount={readCount}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />

          <div className="email-inbox-main">
            <div className="email-inbox-mobile-topbar">
              <button type="button" onClick={() => setSidebarOpen(true)} aria-label={elakayAutoText("h_emk_inbox_open_menu", "Open Inbox menu")}>
                <MdMenu />
              </button>
              <strong>{isViewingEmail ? (openEmail?.subject || elakayAutoText("h_emk_inbox_no_subject", "(no subject)")) : elakayAutoText("h_emk_inbox_page_title", "Email Inbox")}</strong>
            </div>

            {isViewingEmail ? (
              <EmailReceivedView email={openEmail} error={openError} onBack={backToList} />
            ) : (
              <>
                <div className="email-inbox-list-header">
                  <div>
                    <h1><MdMailOutline /> {elakayAutoText("h_emk_inbox_page_title", "Email Inbox")}</h1>
                    {result.unreadCount > 0 && <small>{result.unreadCount.toLocaleString()} {elakayAutoText("h_emk_inbox_unread_suffix", "unread")}</small>}
                  </div>
                  {result.unreadCount > 0 && (
                    <button type="button" className="email-inbox-mark-all" onClick={markAllRead}>
                      <MdMarkEmailRead /> {elakayAutoText("h_emk_inbox_mark_all_read", "Mark all as read")}
                    </button>
                  )}
                </div>

                {error && <div className="emk-error-banner">{error}</div>}

                <div className="email-inbox-list-scroll">
                  {result.unreadCount > 0 && (
                    <button type="button" className="email-inbox-mark-all email-inbox-mark-all-mobile" onClick={markAllRead}>
                      <MdMarkEmailRead /> {elakayAutoText("h_emk_inbox_mark_all_read", "Mark all as read")}
                    </button>
                  )}

                  {loading && !result.items.length && (
                    <ul className="email-inbox-list" aria-busy="true">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <li key={i} className="email-inbox-skeleton-row">
                          <span />
                          <span className="email-inbox-skeleton-bar" style={{ width: "70%" }} />
                          <span className="email-inbox-skeleton-bar" style={{ width: "85%" }} />
                          <span className="email-inbox-skeleton-bar" style={{ width: 40 }} />
                        </li>
                      ))}
                    </ul>
                  )}

                  {!loading && !result.items.length && (
                    <div className="email-inbox-empty">
                      <MdInbox aria-hidden="true" />
                      <strong>
                        {filter === "unread"
                          ? elakayAutoText("h_emk_inbox_empty_unread", "No unread emails.")
                          : filter === "read"
                            ? elakayAutoText("h_emk_inbox_empty_read", "No read emails.")
                            : elakayAutoText("h_emk_inbox_empty_title", "Inbox is empty")}
                      </strong>
                      <span>{elakayAutoText("h_emk_inbox_empty", "You don't have any emails yet.")}</span>
                    </div>
                  )}

                  {!!result.items.length && (
                    <ul className="email-inbox-list">
                      {result.items.map((item) => (
                        <li
                          key={item._id}
                          className={`email-inbox-row ${item.isRead ? "is-read" : "is-unread"}`}
                          onClick={() => openMail(item._id)}
                          role="button"
                          tabIndex={0}
                          aria-label={`${senderDisplayName(item.senderId)} — ${item.subject || elakayAutoText("h_emk_inbox_no_subject", "(no subject)")}`}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openMail(item._id); } }}
                        >
                          {!item.isRead && <span className="email-inbox-dot" aria-hidden="true" />}
                          <span className="email-inbox-sender">{senderDisplayName(item.senderId)}</span>
                          <span className="email-inbox-subject" data-preview={item.previewText}>{item.subject || elakayAutoText("h_emk_inbox_no_subject", "(no subject)")}</span>
                          <span className="email-inbox-preview">{item.previewText}</span>
                          <span className="email-inbox-date">{formatDate(item.sentAt || item.createdAt)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {result.totalPages > 1 && (
                  <div className="email-inbox-pagination">
                    <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label={elakayAutoText("h_emk_inbox_prev_page", "Previous page")}>‹</button>
                    <span>{page} / {result.totalPages}</span>
                    <button type="button" disabled={page >= result.totalPages} onClick={() => setPage((p) => p + 1)} aria-label={elakayAutoText("h_emk_inbox_next_page", "Next page")}>›</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmailInboxPage;
